import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  AlertTitle
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

interface RegenerateConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  caseName: string;
  existingFilesCount: number;
  existingRunsCount: number;
}

export default function RegenerateConfirmationDialog({
  open,
  onClose,
  onConfirm,
  caseName,
  existingFilesCount,
  existingRunsCount
}: RegenerateConfirmationDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Regenerate All Documents?</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>This action will permanently delete existing files</AlertTitle>
          This case already has generated documents and files. Regenerating will:
        </Alert>

        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li>
            <Typography variant="body2">
              <strong>Soft-delete {existingFilesCount} existing files</strong> (generated files, edited versions, and uploaded files)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <strong>Remove {existingRunsCount} previous generation runs</strong>
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <strong>Generate fresh documents</strong> based on current case data
            </Typography>
          </li>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Case:</strong> {caseName}
          </Typography>
        </Alert>

        <Typography variant="body2" color="text.secondary">
          Files will be marked as deleted but remain in storage for recovery purposes. 
          This action cannot be undone from the interface.
        </Typography>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          color="warning" 
          variant="contained"
          startIcon={<WarningIcon />}
        >
          Yes, Regenerate All Documents
        </Button>
      </DialogActions>
    </Dialog>
  );
}